import { ref, computed, toRaw, MaybeRef, unref, watch } from 'vue';

export interface Address {
  latitude?: number;
  longitude?: number;
  [key: string]: unknown;
}

export interface RepairLocation {
  id?: string;
  name?: string;
  short_code?: string;
  address?: Address;
  business?: Business;
  opening_hours?: OpeningHour[];
  services?: Service[];
  primary_cover_image?: string;
  description?: string;
  telephone?: string;
  enabled?: boolean;
  google_link?: string;
  is_primary?: boolean;
  type?: null;
  latitude?: number;
  longitude?: number;
  created_at?: Date;
  updated_at?: Date;

  is_active?: boolean;
  selected?: boolean;
  client_shortlisted?: boolean;
  certified?: boolean;
  distance?: number;
  locations?: RepairLocation[];
}

export interface RequiredFilters {
  latitude?: number;
  longitude?: number;
  radius?: number;
  radius_type?: string;
  brands?: string;
  address?: string;
  [key: string]: unknown;
}

export interface OptionalFilters {
  client_network_only?: boolean;
  services?: string[] | number[];
  location_type?: string | number;
  automaker_certification?: string;
  insurer_certification?: string;
  other_certification?: string;
  brands?: string[];
  [key: string]: unknown;
}

export interface Incident {
  location?: { latitude?: number; longitude?: number };
  [key: string]: unknown;
}

export interface Contact {
  address?: { latitude?: number; longitude?: number };
  [key: string]: unknown;
}

export interface UseRepairFinderOptions {
  location?: MaybeRef<RepairLocation | undefined>;
  repairFinderUrl?: string;
  accidentLocation?: MaybeRef<ILocationInfo | undefined>;
  homeLocation?: MaybeRef<ILocationInfo | undefined>;
}

function createQueryString(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      value.forEach((item) => qs.append(`${key}[]`, String(item)));
    } else {
      qs.append(key, String(value));
    }
  }

  const result = qs.toString();
  return result ? `?${result}` : '';
}

async function fetchJson<T = unknown>(url: string, config?: RequestInit): Promise<T> {
  const response = await fetch(url, config);
  if (!response.ok) {
    throw new Error(`Fetch error ${response.status}: ${response.statusText}`);
  }
  try {
    return (await response.json()) as T;
  } catch (err) {
    throw new Error('Failed to parse JSON response.');
  }
}

import IncidentMarker from 'src/assets/images/map/vehicle_marker.svg';
import HomeMarker from 'src/assets/images/map/home_marker.svg';
import ActiveMarkerWithStar from 'src/assets/images/map/active_marker_with_star.svg';
import ActiveMarker from 'src/assets/images/map/active_marker.svg';
import SelectedMarker from 'src/assets/images/map/selected_marker.svg';
import DarkMarkerWithStar from 'src/assets/images/map/dark_marker_with_star.svg';
import DarkMarker from 'src/assets/images/map/dark_marker.svg';
import GrayMarkerWithStar from 'src/assets/images/map/gray_marker_with_star.svg';
import GrayMarker from 'src/assets/images/map/gray_marker.svg';
import { config } from 'src/utils/config';
import { Business, OpeningHour, Service } from 'src/types/repairFinder';
import { ILocationInfo } from 'src/utils/map';

function getIcon(client_shortlisted?: boolean, certified?: boolean, selected?: boolean, is_active?: boolean): string {
  if (selected) return SelectedMarker;
  if (is_active) {
    if (client_shortlisted) return ActiveMarkerWithStar;
    return ActiveMarker;
  }
  if (certified) {
    if (client_shortlisted) return DarkMarkerWithStar;
    return DarkMarker;
  }
  if (client_shortlisted) return GrayMarkerWithStar;
  return GrayMarker;
}

interface MarkerDetail {
  index: number;
  latitude?: number;
  longitude?: number;
  icon?: string;
  is_active?: boolean;
  size?: number;
  type: 'default' | 'custom';
}

function getSize(is_active?: boolean, client_shortlisted?: boolean): number {
  if (is_active && client_shortlisted) return 1.6;
  if (is_active) return 1.4;
  if (client_shortlisted) return 1.2;
  return 1;
}

const repairLocations = ref<RepairLocation[]>([]);
const iconMap: Record<string, string> = {};

const mapRef = ref<google.maps.Map | null>(null);
const markerInstances = ref<google.maps.marker.AdvancedMarkerElement[]>([]);
let bounds: google.maps.LatLngBounds;
const isLoading = ref(false);
export function useRepairFinder(options: UseRepairFinderOptions = {}) {
  const { repairFinderUrl = env.APP_REPAIR_FINDER_URL } = options;
  const location = computed(() => unref(options.location));
  const accidentLocation = computed(() => unref(options.accidentLocation));
  const homeLocation = computed(() => unref(options.homeLocation));
  bounds = new google.maps.LatLngBounds();
  const selectedRepairLocation = ref<RepairLocation>();
  const requiredFilters = ref<RequiredFilters>({});
  const optionalFilters = ref<OptionalFilters>({});
  const savedLocation = ref<RepairLocation>();

  const getRepairLocations = computed(() => repairLocations.value);
  const getSelectedLocation = computed(() => selectedRepairLocation.value);
  const getSavedLocation = computed(() => savedLocation.value);
  const getRequiredFilters = computed(() => requiredFilters.value);
  const getOptionalFilters = computed(() => optionalFilters.value);

  const activeLocation = computed(() => {
    return repairLocations.value.find((loc) => loc.is_active) || null;
  });
  const activeMarkerDetail = computed<MarkerDetail | null>(() => {
    return markerDetails.value.find((loc) => loc.is_active) || null;
  });

  const markerDetails = computed<MarkerDetail[]>(() => {
    const details: Array<MarkerDetail> = [];

    repairLocations.value.forEach((loc, index) => {
      const lat = loc.address?.latitude;
      const lng = loc.address?.longitude;
      if (lat !== undefined && lng !== undefined) {
        details.push({
          latitude: lat,
          longitude: lng,
          icon: getIcon(loc.client_shortlisted, loc.certified, loc.selected, loc.is_active),
          is_active: loc.is_active,
          size: getSize(loc.is_active, loc.client_shortlisted),
          index,
          type: 'default',
        });
      }
    });

    if (accidentLocation.value?.latitude && accidentLocation.value?.longitude) {
      details.push({
        latitude: Number(accidentLocation.value.latitude),
        longitude: Number(accidentLocation.value.longitude),
        icon: IncidentMarker,
        size: 1.2,
        index: details.length,
        type: 'custom',
      });
    }

    if (homeLocation.value?.latitude && homeLocation.value?.longitude) {
      details.push({
        latitude: Number(homeLocation.value.latitude),
        longitude: Number(homeLocation.value.longitude),
        icon: HomeMarker,
        size: 1.2,
        index: details.length,
        type: 'custom',
      });
    }

    return details;
  });
  watch(
    markerDetails,
    (newDetails, oldDetails) => {
      if (!mapRef.value || !oldDetails || newDetails.length !== oldDetails.length) {
        return; // Let setMarkers handle it
      }

      newDetails.forEach(async (detail, index) => {
        const oldDetail = oldDetails[index];
        const marker = markerInstances.value[index];

        if (!marker || !oldDetail) return;

        if (
          detail.icon !== oldDetail.icon ||
          detail.size !== oldDetail.size ||
          detail.is_active !== oldDetail.is_active
        ) {
          if (detail.icon && detail.size) {
            try {
              const parser = new DOMParser();
              let iconText: string;
              if (iconMap[detail.icon]) {
                iconText = iconMap[detail.icon];
              } else {
                iconText = await (await fetch(detail.icon)).text();
                iconMap[detail.icon] = iconText;
              }
              const svgElement = parser.parseFromString(iconText, 'image/svg+xml').documentElement;
              svgElement.style.width = `${25 * detail.size}px`;
              svgElement.style.height = `${35 * detail.size}px`;
              marker.content = svgElement;
              marker.zIndex = detail.is_active ? 10 : 1;
            } catch (e) {
              console.error('Failed to fetch or parse SVG icon', e);
            }
          }
        }
      });
    },
    { deep: true }
  );

  function updateRepairLocations(newLocations: RepairLocation[]): void {
    const activeLoc = repairLocations.value.find((l) => l.is_active);

    const extended = newLocations
      .filter((l) => l?.address?.latitude !== undefined)
      .map((l) => ({
        ...l,
        address: {
          ...l.address,
          latitude: parseFloat(String(l.address?.latitude ?? 0)),
          longitude: parseFloat(String(l.address?.longitude ?? 0)),
        },
        is_active: l.id === activeLoc?.id,
        selected: false,
      }));

    repairLocations.value = extended;

    if (savedLocation.value?.id) {
      repairLocations.value = repairLocations.value.filter((l) => l.id !== savedLocation.value?.id);
      repairLocations.value.unshift(savedLocation.value);
    }
  }

  function toggleRepairLocationState(config: { index: number | string; property: keyof RepairLocation }) {
    repairLocations.value.forEach((element: RepairLocation) => (element.is_active = false));
    let idx: number;
    if (typeof config.index !== 'number') {
      idx = repairLocations.value.findIndex((location) => location.id === config.index);
    } else {
      idx = config.index;
    }
    if (idx >= 0) {
      repairLocations.value[idx].is_active = true;
      if (config.property === 'selected') {
        repairLocations.value.forEach((element) => (element.is_active = false));
        repairLocations.value[idx].is_active = true;
      }
      return {
        location: repairLocations.value[idx],
        detail: markerDetails.value[idx],
        index: idx,
      };
    }
    return {
      location: null,
      detail: null,
      index: -1,
    };
  }

  function updateSelectedRepairLocation(location: RepairLocation): void {
    try {
      const currSelected = repairLocations.value.find((loc) => loc.selected);

      const lat = parseFloat(String(location.address?.latitude ?? 0));
      const lng = parseFloat(String(location.address?.longitude ?? 0));

      const extendedLoc: RepairLocation = {
        ...location,
        selected: location.id === currSelected?.id,
        is_active: !!location.is_primary,
        address: { ...location.address, latitude: lat, longitude: lng },
        locations:
          location.locations?.map((loc) => {
            const cLat = parseFloat(String(loc?.address?.latitude ?? 0));
            const cLng = parseFloat(String(loc?.address?.longitude ?? 0));
            return {
              ...loc,
              address: { ...loc?.address, latitude: cLat, longitude: cLng },
              is_active: loc.is_primary,
              selected: loc.id === currSelected?.id,
            };
          }) ?? [],
      };

      if (!extendedLoc.locations?.length) {
        extendedLoc.locations = [{ ...extendedLoc }];
      }

      selectedRepairLocation.value = extendedLoc;
      repairLocations.value = extendedLoc.locations;
    } catch (err) {
      console.error('updateSelectedRepairLocation error:', err);
    }
  }

  function updateSavedLocation(location: RepairLocation): void {
    savedLocation.value = location;
  }

  function setRequiredFilters(filters: Partial<RequiredFilters>): void {
    requiredFilters.value = { ...requiredFilters.value, ...filters };
  }

  function setOptionalFilters(filters: Partial<OptionalFilters>): void {
    optionalFilters.value = { ...optionalFilters.value, ...filters };
  }

  async function searchRepairLocations(): Promise<void> {
    updateRepairLocations([]);
    isLoading.value = true;
    const params = { ...requiredFilters.value, ...optionalFilters.value };
    if (config.value.settings?.repairBrands?.length) {
      const brandIds = config.value.settings?.repairBrands.map((brand) => String(brand.id));
      params.brands = brandIds;
    }

    delete params.address;
    delete params.radius_type;
    delete params.automaker_certification;
    delete params.insurer_certification;
    delete params.other_certification;

    const qs = createQueryString(params);
    const url = `${repairFinderUrl}/search${qs}`;

    try {
      const data = await fetchJson<{ data: RepairLocation[] }>(url, { method: 'GET' });
      const results = data && typeof data === 'object' && 'data' in data ? data?.data : data;
      if (Array.isArray(results)) {
        updateRepairLocations(results);
        if (location.value?.id) {
          toggleRepairLocationState({ index: location.value.id, property: 'is_active' });
        }
      } else {
        updateRepairLocations([]);
      }
    } catch (err) {
      console.warn('searchRepairLocations error:', err);
      updateRepairLocations([]);
    } finally {
      isLoading.value = false;
    }
  }

  async function getRepairLocationInfo(id: string): Promise<void> {
    const url = `${repairFinderUrl}/locations/${id}`;
    try {
      const data = await fetchJson<unknown>(url);
      const locData = data && typeof data === 'object' && 'data' in data ? data?.data : data;
      updateSelectedRepairLocation(locData as RepairLocation);
    } catch (err) {
      console.error('getRepairLocationInfo error:', err);
    }
  }

  async function getRepairLocationBrands(): Promise<unknown[]> {
    try {
      const url = `${repairFinderUrl}/brands`;
      const data = await fetchJson<unknown>(url);
      const arr = data && typeof data === 'object' && 'data' in data ? data?.data : data;
      return Array.isArray(arr) ? arr : [];
    } catch (err) {
      console.error('getRepairLocationBrands error:', err);
      return [];
    }
  }

  async function getRepairLocationFilters(): Promise<{
    location_types?: unknown;
    services?: unknown;
    certifications?: unknown;
  }> {
    try {
      const [locResp, svcResp, certResp] = await Promise.all([
        fetchJson<unknown>(`${repairFinderUrl}/location-types`),
        fetchJson<unknown>(`${repairFinderUrl}/services`),
        fetchJson<unknown>(`${repairFinderUrl}/certifications`),
      ]);
      return {
        location_types: locResp && typeof locResp === 'object' && 'data' in locResp ? locResp?.data : locResp,
        services: svcResp && typeof svcResp === 'object' && 'data' in svcResp ? svcResp?.data : svcResp,
        certifications: certResp && typeof certResp === 'object' && 'data' in certResp ? certResp?.data : certResp,
      };
    } catch (err) {
      console.error('getRepairLocationFilters error:', err);
      return {};
    }
  }

  async function getRepairLocationSavedFilters(params: Record<string, unknown>): Promise<unknown> {
    const qs = createQueryString(params);
    const url = `${repairFinderUrl}/filter-store${qs}`;
    try {
      const data = await fetchJson<unknown>(url);
      return data;
    } catch (err) {
      console.error('getRepairLocationSavedFilters error:', err);
      return {};
    }
  }

  function selectLocation(index: number) {
    repairLocations.value.forEach((loc) => {
      loc.selected = false;
      loc.is_active = false;
    });
    if (index >= 0 && index < repairLocations.value.length) {
      repairLocations.value[index].selected = true;
      repairLocations.value[index].is_active = true;
      const chosen = repairLocations.value[index];
      repairLocations.value.splice(index, 1);
      repairLocations.value.unshift(chosen);
      savedLocation.value = chosen;
    }
  }

  function deselectLocation() {
    repairLocations.value.forEach((loc) => {
      loc.selected = false;
      loc.is_active = false;
    });
    savedLocation.value = {};
  }

  async function setMarkers(firstLoad = false): Promise<void> {
    if (!mapRef.value) return;
    deleteMarkers();
    bounds = new google.maps.LatLngBounds();

    const newMarkers: (google.maps.marker.AdvancedMarkerElement | undefined)[] = [];

    const createdMarkers = await Promise.all(markerDetails.value.map((detail, index) => createMarker(detail, index)));
    createdMarkers.forEach((marker, index) => {
      newMarkers[index] = marker;
    });

    markerInstances.value = newMarkers.filter((m) => m) as google.maps.marker.AdvancedMarkerElement[];

    if (firstLoad && markerInstances.value.length > 0) {
      mapRef.value.fitBounds(bounds);
    }
  }

  function deleteMarkers() {
    markerInstances.value.forEach((marker) => {
      if (marker) marker.map = null;
    });
    markerInstances.value = [];
  }

  async function createMarker(
    detail: MarkerDetail,
    index: number
  ): Promise<google.maps.marker.AdvancedMarkerElement | undefined> {
    if (!mapRef.value) return;
    const { latitude, longitude, icon, size, is_active, type } = detail;
    if (latitude === undefined || longitude === undefined || !icon || !size) return;

    const position = new google.maps.LatLng(latitude, longitude);

    const parser = new DOMParser();
    let iconText: string;
    if (iconMap[icon]) {
      iconText = iconMap[icon];
    } else {
      iconText = await (await fetch(icon)).text();
      iconMap[icon] = iconText;
    }
    const svgElement = parser.parseFromString(iconText, 'image/svg+xml').documentElement;
    svgElement.style.width = `${25 * size}px`;
    svgElement.style.height = `${35 * size}px`;

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: toRaw(mapRef.value),
      position,
      content: svgElement,
      zIndex: is_active ? 10 : 1,
    });

    if (type === 'default') {
      marker.addListener('gmp-click', () => {
        setActiveMarker(detail, index);
      });
    }

    bounds.extend(marker.position!);
    return marker;
  }

  function setActiveMarker(detail: MarkerDetail, idx?: number) {
    const indexToActivate = idx !== undefined ? idx : detail.index;

    if (detail.type === 'custom') return;

    // Prevent re-activating the same marker.
    if (activeMarkerDetail.value?.index === indexToActivate) return;

    toggleRepairLocationState({ index: indexToActivate, property: 'is_active' });
  }

  return {
    repairLocations,
    mapRef,
    isLoading,
    getRepairLocations,
    getSelectedLocation,
    getSavedLocation,
    getRequiredFilters,
    getOptionalFilters,
    activeLocation,
    activeMarkerDetail,
    requiredFilters,
    markerDetails,
    updateRepairLocations,
    toggleRepairLocationState,
    updateSelectedRepairLocation,
    updateSavedLocation,
    setRequiredFilters,
    setOptionalFilters,
    searchRepairLocations,
    getRepairLocationInfo,
    getRepairLocationBrands,
    getRepairLocationFilters,
    getRepairLocationSavedFilters,
    selectLocation,
    deselectLocation,
    setMarkers,
    setActiveMarker,
  };
}
